import { useState } from 'react';
import { Tv } from 'lucide-react';

const AVATAR_COLORS = [
  'bg-red-600',
  'bg-orange-600',
  'bg-amber-600',
  'bg-yellow-600',
  'bg-lime-600',
  'bg-green-600',
  'bg-emerald-600',
  'bg-teal-600',
  'bg-cyan-600',
  'bg-sky-600',
  'bg-blue-600',
  'bg-indigo-600',
  'bg-violet-600',
  'bg-purple-600',
  'bg-fuchsia-600',
  'bg-pink-600',
  'bg-rose-600',
];

function hashName(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function initialsFor(name: string): string {
  const cleaned = name
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\[[^\]]*\]/g, ' ')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const words = cleaned.split(' ').filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) {
    const w = words[0];
    return (w[0] + (w[1] || '')).toUpperCase();
  }
  return (words[0][0] + words[1][0]).toUpperCase();
}

interface LogoProps {
  name: string;
  url?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function Logo({ name, url, className = '', size = 'md' }: LogoProps) {
  const [failed, setFailed] = useState(false);
  const color = AVATAR_COLORS[hashName(name) % AVATAR_COLORS.length];
  const initials = initialsFor(name);
  const textSize = size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-2xl' : 'text-lg';

  if (!url || failed) {
    return (
      <div
        className={`flex items-center justify-center text-white font-bold ${textSize} ${color} ${className}`}
        title={name}
      >
        {initials || <Tv className="w-1/2 h-1/2" />}
      </div>
    );
  }

  // Route external logos through the Cloudflare Pages Function proxy.
  // Many logo hosts (especially i.imgur.com) aggressively rate-limit
  // browser image requests — when 20+ logos render at once, imgur
  // starts returning 429. Going through /proxy fetches the image
  // server-to-server from Cloudflare's IP, which isn't rate-limited.
  // The Cloudflare Function in functions/proxy.ts already handles
  // this; in `pnpm run dev` the Vite plugin in vite.config.ts serves
  // the same route from an in-memory cache.
  const proxiedSrc = `/proxy?url=${encodeURIComponent(url)}`;

  return (
    <img
      src={proxiedSrc}
      alt={name}
      className={`object-contain bg-white ${className}`}
      onError={() => setFailed(true)}
      loading="lazy"
    />
  );
}